import { useState } from 'react';
import PropTypes from 'prop-types';
import PhoneInput from 'react-phone-input-2';
import { Pencil, XCircle, CheckCircle } from 'lucide-react';
import clsx from 'clsx';
import 'react-phone-input-2/lib/style.css';

/** Shared Glassmorphic Wrapper */
const GlassWrapper = ({ children }) => (
  <div className="p-4 rounded-xl backdrop-blur-md bg-white/5 border border-white/10 shadow-md transition hover:bg-white/10 dark:bg-white/5">
    {children}
  </div>
);

/**
 * EditablePhoneField – Upgraded for premium UI.
 * - Animated edit mode with soft hover states
 * - Glassmorphism styling
 * - Press Enter or check icon to save
 */
export const EditablePhoneField = ({ label, name, value, onSubmit }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [phoneValue, setPhoneValue] = useState(value || '');
  const [loading, setLoading] = useState(false);

  const saveValue = async () => {
    if (phoneValue !== value) {
      setLoading(true);
      await onSubmit({ [name]: phoneValue });
      setLoading(false);
    }
    setIsEditing(false);
  };

  const cancelEdit = () => {
    setPhoneValue(value || '');
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') saveValue();
    if (e.key === 'Escape') cancelEdit();
  };

  return (
    <GlassWrapper>
      <div className="flex flex-col space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-xs text-gray-300 dark:text-gray-400">{label}</label>
          {!isEditing && (
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="text-gray-400 hover:text-blue-400 transition"
            >
              <Pencil size={14} />
            </button>
          )}
        </div>

        {isEditing ? (
          <div className="flex flex-col space-y-3">
            <PhoneInput
              country="us"
              value={phoneValue}
              onChange={(val) => setPhoneValue(val)}
              onKeyDown={handleKeyDown}
              inputStyle={{
                width: '100%',
                fontSize: '0.875rem',
                padding: '0.5rem 2.5rem',
                borderRadius: '0.5rem',
                border: '1px solid rgba(255,255,255,0.1)',
                background: 'rgba(255,255,255,0.05)',
                color: '#fff',
              }}
              buttonStyle={{
                background: 'transparent',
                border: 'none',
              }}
              dropdownStyle={{ background: '#1f2937', color: '#fff' }}
            />
            <div className="flex items-center gap-3">
              <button
                onClick={saveValue}
                className={clsx(
                  'flex items-center px-3 py-1 rounded-lg text-sm text-white bg-green-500 hover:bg-green-600 transition',
                  loading && 'opacity-50 pointer-events-none'
                )}
              >
                <CheckCircle className="h-4 w-4 mr-1" /> Save
              </button>
              <button
                onClick={cancelEdit}
                className="flex items-center px-3 py-1 rounded-lg text-sm text-white bg-red-500 hover:bg-red-600 transition"
              >
                <XCircle className="h-4 w-4 mr-1" /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <p
            className="text-sm font-semibold cursor-pointer text-white hover:text-blue-300 transition"
            onClick={() => setIsEditing(true)}
          >
            {value || <em className="text-gray-400">No phone number</em>}
          </p>
        )}
      </div>
    </GlassWrapper>
  );
};

EditablePhoneField.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
};